import { Button, HStack, Icon, Modal, Spinner, VStack } from "@/shared/ui/primitives";
import { useTranslation } from "react-i18next";

const REFILL_OPTIONS = [
	{ count: 10, price: 200 },
	{ count: 30, price: 600 },
	{ count: 50, price: 1000 },
	{ count: 100, price: 1900 },
	{ count: 200, price: 3800 },
	{ count: 300, price: 5400 },
] as const;

const formatPrice = (price: number) =>
	`₩${price.toLocaleString("ko-KR")}`;

export interface GyulRefillModalProps {
	isOpen: boolean;
	onClose: () => void;
	onPurchase: (count: number, price: number) => void;
	autoChargeEnabled: boolean;
	onAutoChargeToggle: () => void;
	isLoading?: boolean;
}

export const GyulRefillModal = ({
	isOpen,
	onClose,
	onPurchase,
	autoChargeEnabled,
	onAutoChargeToggle,
	isLoading,
}: GyulRefillModalProps) => {
	const { t } = useTranslation();
	return (
		<Modal className="w-full max-w-[480px]" isOpen={isOpen} onClose={onClose}>
			<VStack className="w-full p-[20px] gap-[6px] rounded-[24px] bg-bg-weak/95 backdrop-blur-md shadow-[0_24px_80px_rgba(15,23,42,0.12)]">
				<HStack className="w-full justify-between items-center p-[4px]">
					<HStack className="gap-[6px] text-pm-strong">
						<span className="text-xl font-semibold text-ct-strong">{t("workflow.refillTitle")}</span>
					</HStack>
					<button type="button" onClick={onClose} className="p-[6px] rounded-[10px] text-ct-strong">
						<Icon name="Close" width={24} height={24} />
					</button>
				</HStack>
				<VStack className="w-full gap-[12px]">
					{REFILL_OPTIONS.map(({ count, price }) => (
						<HStack
							key={count}
							className="w-full p-[10px] pl-[16px] rounded-[14px] bg-bg-regular items-center"
						>
							<HStack className="flex-1 gap-[4px] items-center justify-start text-ct-strong">
								<Icon name="GyulLeft" width={20} height={20} />
								<span className="text-md font-medium">{t("workflow.refillItemLabel", { count })}</span>
							</HStack>
							<Button
								variant="primary"
								size="sm"
								onClick={() => onPurchase(count, price)}
								className="w-[90px]"
								disabled={isLoading}
							>
								{isLoading ? (
									<Spinner size="sm" />
								) : (
									<span className="text-sm font-medium text-ct-elevated">
										{formatPrice(price)}
									</span>
								)}
							</Button>
						</HStack>
					))}
				</VStack>
				<div className="w-full h-px bg-outline my-[6px]" />
				<HStack className="w-full px-[4px] py-[6px] justify-between items-center">
					<VStack className="items-start gap-[2px]">
						<span className="text-sm font-medium text-ct-strong">{t("workflow.autoCharge")}</span>
						<span className="text-xs text-ct-regular">{t("workflow.autoChargeDescription")}</span>
					</VStack>
					<button
						type="button"
						onClick={onAutoChargeToggle}
						className={`relative w-[44px] h-[24px] rounded-full transition-colors ${autoChargeEnabled ? "bg-pm-strong" : "bg-bg-regular"}`}
					>
						<span
							className={`absolute top-[2px] w-[20px] h-[20px] rounded-full bg-white shadow transition-transform ${autoChargeEnabled ? "left-[22px]" : "left-[2px]"}`}
						/>
					</button>
				</HStack>
			</VStack>
		</Modal>
	);
};
